import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import moment from "moment";
import ParentHeader from "../ParentHeader/ParentHeader";
import Help from "../../Components/Help/Help";
import Loader from "../../Components/Loader/Loader";
import { serviceConsumer } from "../../network/ServiceConsumer";
import { groupAndSortComp } from "../../helper/SortSyllabus";
import { formatDate } from "../../helper/formatDate";
import { useMaterialSymbols } from "../../Teacher/Oireachtas/components";
import { getWithSession, isAuthFailure, printWithSession } from "./sessionRequests";
import {
  normalizeResults, dancersWithEntries, compMatches, levelsOf, isEventDay, isJustPublished, viewFor, includesFolded,
  isLoaded, RESULT_ERROR,
} from "./resultsModel";
import { CompetitionCard, DancerCards, JudgedCard } from "./ResultBlocks";
import "../../Teacher/Oireachtas/oireachtas.css";
import "./eventResults.css";

// Feis/grade event results (card 34022f7d; locked mockup v52, Sunday cut).
// Views: spectator (signed out, an organizer, or a family with no dancer at
// the event: every published competition, placers only); parent and teacher
// (My Results tab by default: one card per own dancer / student with their
// competitions; All Results: the spectator list). The server trims what each
// viewer may see (/public: placers, no comments; /viewer: + own dancers'
// rows with comments; a teacher's full field) - this page only lays it out.
// On the event day the page refreshes every minute while it is visible.
// An adjudicator also gets "My adjudications" (card 930d3ac0, part 2): the
// published competitions they marked here, each the full table from /judge
// with their column highlighted and their own comments a tap away.

const BASE = () => process.env.REACT_APP_BASE_URL;
const REFRESH_MS = 60 * 1000;
const PARALLEL = 6;

const EventResults = () => {
  useMaterialSymbols();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const eventId = params.get("id");
  // The "Result published" email links to one competition (cid): open it.
  const focusCompId = params.get("cid");

  // Signed in until a signed-in read says the session has ended; then this
  // page carries on with the public reads (sessionRequests.js).
  const [signedIn, setSignedIn] = useState(!!localStorage.getItem("accessToken"));
  const organizer = !!localStorage.getItem("organizer");
  const isTeacher = localStorage.getItem("isTeacher") === "true";
  const isAdjudicator = localStorage.getItem("isAdjudicator") === "true";

  const [initialLoading, setInitialLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [comps, setComps] = useState([]);
  const [dancers, setDancers] = useState([]);
  const [results, setResults] = useState({}); // compId -> normalized | "error"
  const [tab, setTab] = useState(params.get("tab") === "judged" ? "judged" : focusCompId ? "all" : "mine");
  // My adjudications: the competitions they marked here (null until known),
  // their own results (kept apart from the family/public cache), the open one
  // and the dancer whose marks are showing
  const [judged, setJudged] = useState(null);
  const [judgeResults, setJudgeResults] = useState({});
  const [openJudged, setOpenJudged] = useState(null);
  const [picked, setPicked] = useState(null);
  const openJudgedRef = useRef(null);
  openJudgedRef.current = openJudged;
  const [openComp, setOpenComp] = useState(focusCompId || null);
  const [openMine, setOpenMine] = useState(null);
  const [q, setQ] = useState("");
  const [tq, setTq] = useState("");
  const [level, setLevel] = useState("All");
  const [flipped, setFlipped] = useState(() => new Set());
  const [updatedAt, setUpdatedAt] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [, setTick] = useState(0);

  const signedInRef = useRef(signedIn);
  signedInRef.current = signedIn;
  // bumped when the session ends: viewer (personal) reads started before
  // that are dropped when they land
  const sessionGen = useRef(0);
  const inFlight = useRef(new Set());
  const again = useRef(new Set());
  const compsRef = useRef([]);
  compsRef.current = comps;
  const resultsRef = useRef(results);
  resultsRef.current = results;

  const view = viewFor({ signedIn, organizer, isTeacher, dancers });
  const family = view === "parent" || view === "teacher";
  // Personal reads only for the family views: a signed-in organizer or a
  // session with no dancer at the event is a spectator and reads /public.
  const familyRef = useRef(family);
  familyRef.current = family;

  // ---- data ----
  // The session ended (a signed-in read got 401/403): carry on as a
  // spectator. Personal (/viewer) results already cached - own non-placers,
  // comments, a teacher's full field - are dropped so they are neither shown
  // nor searchable any more; public results stay.
  const downgrade = useCallback(() => {
    if (!signedInRef.current) return;
    signedInRef.current = false;
    sessionGen.current += 1;
    setSignedIn(false);
    setResults((prev) => Object.fromEntries(Object.entries(prev).filter(([, r]) => isLoaded(r) && r.source === "public")));
    // a judge's own results are personal too
    setJudged(null);
    setJudgeResults({});
  }, []);

  // One competition's result: /viewer for a signed-in family view, else
  // /public. source: which read it came from; partial: the /viewer read
  // failed for another reason (e.g. 500), so only the public placers are
  // known - own dancers show "not loaded, try again", never "no result".
  const fetchResult = useCallback(async (compId) => {
    const gen = sessionGen.current;
    const comp = compsRef.current.find((c) => c._id === compId);
    const url = `${BASE()}/feis/competition/scores/${compId}`;
    let response;
    let source = "public";
    let partial = false;
    if (signedInRef.current && familyRef.current) {
      try {
        response = await getWithSession(`${url}/viewer`);
        source = "viewer";
      } catch (e) {
        if (isAuthFailure(e)) downgrade();
        else partial = true;
      }
    }
    if (!response) response = await serviceConsumer("GET", `${url}/public`);
    return { gen, result: { ...normalizeResults(comp, response), source, partial: partial && signedInRef.current } };
  }, [downgrade]);

  // Load (or reload) results for these competitions, a few at a time. A
  // failed load leaves the "error" sentinel (retryable); a personal result
  // that lands after the session ended is dropped.
  const loadResults = useCallback(async (ids, { force = false } = {}) => {
    const unique = [...new Set(ids)];
    // a forced (re)load of one already loading runs again when that finishes
    // (its request may have started before the fault was fixed)
    if (force) unique.filter((id) => inFlight.current.has(id)).forEach((id) => again.current.add(id));
    const todo = unique.filter((id) => !inFlight.current.has(id));
    todo.forEach((id) => inFlight.current.add(id));
    const queue = [...todo];
    const worker = async () => {
      while (queue.length) {
        const id = queue.shift();
        try {
          const { gen, result } = await fetchResult(id);
          if (!(result.source === "viewer" && gen !== sessionGen.current)) setResults((prev) => ({ ...prev, [id]: result }));
        } catch (e) {
          // a failed revalidation keeps the copy already shown
          setResults((prev) => (isLoaded(prev[id]) ? prev : { ...prev, [id]: RESULT_ERROR }));
        } finally {
          if (again.current.has(id)) {
            again.current.delete(id);
            queue.push(id);
          } else {
            inFlight.current.delete(id);
          }
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(PARALLEL, queue.length) }, worker));
  }, [fetchResult]);

  const dropResults = useCallback((ids) => {
    if (!ids.length) return;
    setResults((prev) => {
      const next = { ...prev };
      ids.forEach((id) => delete next[id]);
      return next;
    });
  }, []);

  const loadComps = useCallback(async () => {
    const res = await serviceConsumer("GET", `${BASE()}/feis/${eventId}/competition`);
    return groupAndSortComp(res?.competitions || []) || [];
  }, [eventId]);

  useEffect(() => {
    window.scroll(0, 0);
    (async () => {
      try {
        let signed = signedIn;
        let eventResponse;
        let registered = [];
        if (signed) {
          try {
            eventResponse = await getWithSession(`${BASE()}/feis/${eventId}`);
            const d = await getWithSession(`${BASE()}/account/get-registered-dancers?feisId=${eventId}`);
            registered = (d?.dancers || []).filter((x) => x.isRegistered);
          } catch (e) {
            if (!isAuthFailure(e)) throw e;
            signed = false;
            downgrade();
          }
        }
        if (!signed) eventResponse = await serviceConsumer("GET", `${BASE()}/feis/single/${eventId}`);
        setEvent(eventResponse?.Feis || null);
        document.title = `Feis.link - ${eventResponse?.Feis?.name || "Results"}`;
        const list = await loadComps();
        compsRef.current = list;
        setComps(list);
        setDancers(registered);
        setUpdatedAt(new Date());
      } catch (e) {
        // the page shows its empty state
      } finally {
        setInitialLoading(false);
      }
    })();
    // once per page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The competitions this adjudicator marked here (published only): loaded
  // with the page and again by the event-day refresh. A reply that lands
  // after the session ended is dropped (it would bring the tab back).
  const loadAdjudicated = useCallback(async () => {
    if (!signedInRef.current || !isAdjudicator) return;
    const gen = sessionGen.current;
    try {
      const res = await getWithSession(`${BASE()}/feis/${eventId}/adjudicated`);
      if (gen !== sessionGen.current || !signedInRef.current) return;
      setJudged(new Set((res?.competitions || []).map((c) => String(c.competition_id))));
    } catch (e) {
      if (isAuthFailure(e)) downgrade();
    }
  }, [eventId, isAdjudicator, downgrade]);
  useEffect(() => {
    if (!initialLoading) loadAdjudicated();
  }, [initialLoading, loadAdjudicated]);

  const loadJudged = useCallback(async (compId) => {
    if (!signedInRef.current) return;
    const gen = sessionGen.current;
    try {
      const res = await getWithSession(`${BASE()}/feis/competition/scores/${compId}/judge`);
      if (gen !== sessionGen.current) return;
      const comp = compsRef.current.find((c) => c._id === compId);
      setJudgeResults((prev) => ({ ...prev, [compId]: normalizeResults(comp, res) }));
    } catch (e) {
      if (isAuthFailure(e)) return downgrade();
      setJudgeResults((prev) => (isLoaded(prev[compId]) ? prev : { ...prev, [compId]: RESULT_ERROR }));
    }
  }, [downgrade]);
  const toggleJudged = (id) => {
    const next = openJudged === id ? null : id;
    setOpenJudged(next);
    setPicked(null);
    if (next) loadJudged(next);
  };
  // only while signed in, and only competitions still published
  const judgedList = useMemo(
    () => (judged && signedIn ? comps.filter((c) => judged.has(c._id) && c.results_published) : []),
    [judged, signedIn, comps]
  );
  // a "judged" link with nothing (yet) to show there falls back to All Results
  const shownTab = tab === "judged" && judgedList.length === 0 ? "all" : tab;

  const mineList = useMemo(() => (family ? dancersWithEntries(dancers, comps) : []), [family, dancers, comps]);
  const published = useMemo(() => comps.filter((c) => c.results_published), [comps]);

  // Places for My Results: every published competition an own dancer is in.
  useEffect(() => {
    if (initialLoading || !family) return;
    const ids = mineList.flatMap((d) => d.comps).filter((c) => c.results_published).map((c) => c._id);
    loadResults(ids.filter((id) => !results[id]));
    // results is read, not a trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoading, family, mineList, loadResults]);

  // The email link's competition: load, then bring it into view.
  const focusedOnce = useRef(false);
  useEffect(() => {
    if (initialLoading || !focusCompId || focusedOnce.current) return;
    focusedOnce.current = true;
    if (comps.some((c) => c._id === focusCompId && c.results_published)) loadResults([focusCompId]);
    setTimeout(() => document.getElementById(`result-${focusCompId}`)?.scrollIntoView({ block: "start" }), 0);
  }, [initialLoading, focusCompId, comps, loadResults]);

  // Searching by dancer, school or card: ONE public request for every frozen
  // published result of the event (GET /feis/:id/results/published, placers
  // only), cached for the page and refreshed by the event-day poll.
  // Competitions published before freezing existed are not in it; those are
  // searched only once their results are loaded (opened) - never fanned out.
  const [bulk, setBulk] = useState(null); // compId -> normalized public result
  const bulkRequested = useRef(false);
  const loadBulk = useCallback(async () => {
    try {
      const res = await serviceConsumer("GET", `${BASE()}/feis/${eventId}/results/published`);
      const map = {};
      (res?.competitions || []).forEach((entry) => {
        const id = String(entry.competition_id);
        map[id] = normalizeResults(compsRef.current.find((c) => c._id === id), entry);
      });
      setBulk(map);
    } catch (e) {
      setBulk((prev) => prev || {}); // older API: codes and opened results only
    }
  }, [eventId]);
  useEffect(() => {
    if (q.trim().length < 2 || bulkRequested.current) return;
    bulkRequested.current = true;
    loadBulk();
  }, [q, loadBulk]);

  // A newer publication than the one cached (the bulk results carry each
  // competition's publishedAt): drop the stale copy, reload it if it is open.
  useEffect(() => {
    if (!bulk) return;
    const stale = Object.entries(resultsRef.current)
      .filter(([id, r]) => isLoaded(r) && r.publishedAt && bulk[id]?.publishedAt && bulk[id].publishedAt !== r.publishedAt)
      .map(([id]) => id);
    if (!stale.length) return;
    dropResults(stale);
    const openIds = [openComp, openMine && openMine.split("|")[1]].filter(Boolean);
    loadResults(stale.filter((id) => openIds.includes(id)), { force: true });
    // only a new bulk index triggers this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulk]);

  // Event day: refresh every minute while the page is visible.
  const eventDay = isEventDay(event);
  const refresh = useCallback(async () => {
    try {
      const before = new Map(compsRef.current.map((c) => [c._id, !!c.results_published]));
      const list = await loadComps();
      const nowPub = new Map(list.map((c) => [c._id, !!c.results_published]));
      const newly = list.filter((c) => c.results_published && before.get(c._id) === false).map((c) => c._id);
      // publication withdrawn: forget the cached result (a republication is
      // a new result) and its "just published" mark
      const withdrawn = [...before].filter(([id, was]) => was && nowPub.get(id) === false).map(([id]) => id);
      compsRef.current = list;
      setComps(list);
      dropResults([...newly, ...withdrawn]);
      // My adjudications follows publication too: forget changed results,
      // reload the open one, and re-read which competitions are theirs
      if (newly.length || withdrawn.length) {
        setJudgeResults((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => !newly.includes(id) && !withdrawn.includes(id))));
      }
      if (openJudgedRef.current && nowPub.get(openJudgedRef.current)) loadJudged(openJudgedRef.current);
      loadAdjudicated();
      // the search index must not keep a withdrawn competition's names, even
      // if the bulk refresh below fails
      if (withdrawn.length) {
        setBulk((prev) => (prev ? Object.fromEntries(Object.entries(prev).filter(([id]) => !withdrawn.includes(id))) : prev));
      }
      if (newly.length || withdrawn.length) {
        setFlipped((prev) => new Set([...prev, ...newly].filter((id) => !withdrawn.includes(id))));
      }
      const mineIds = new Set(mineList.flatMap((d) => d.comps.map((c) => c._id)));
      const openIds = [openComp, openMine && openMine.split("|")[1]].filter(Boolean);
      const wanted = new Set([
        // open competitions follow score edits / republication
        ...openIds,
        // own dancers' competitions: newly published, or a failed / partial load to retry
        ...newly.filter((id) => mineIds.has(id)),
        ...[...mineIds].filter((id) => {
          const r = resultsRef.current[id];
          return r === RESULT_ERROR || (isLoaded(r) && r.partial);
        }),
      ]);
      loadResults([...wanted].filter((id) => nowPub.get(id)), { force: true });
      // the search index follows new publications too
      if (bulkRequested.current) loadBulk();
      setUpdatedAt(new Date());
    } catch (e) {
      // try again next minute
    }
  }, [loadComps, loadResults, loadBulk, dropResults, mineList, openComp, openMine, loadJudged, loadAdjudicated]);

  useEffect(() => {
    if (!eventDay) return undefined;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") refresh();
      setTick((n) => n + 1); // ages "Just published" markers
    }, REFRESH_MS);
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVisible); };
  }, [eventDay, refresh]);

  // After the session ended, an open competition whose personal result was
  // dropped reloads as the public view.
  const wasSignedIn = useRef(signedIn);
  useEffect(() => {
    if (wasSignedIn.current && !signedIn && openComp && !isLoaded(resultsRef.current[openComp])) {
      loadResults([openComp], { force: true });
    }
    wasSignedIn.current = signedIn;
  }, [signedIn, openComp, loadResults]);

  // ---- actions ----
  const retry = (id) => loadResults([id], { force: true });
  // Opening a competition always revalidates it: a cached copy shows at once
  // and is replaced by the current publication (a withdraw + republish
  // between polls, or a score edit, cannot leave an old result showing).
  const toggleComp = (id) => {
    const next = openComp === id ? null : id;
    setOpenComp(next);
    if (next) retry(next);
  };
  const toggleMine = (key, compId) => {
    const next = openMine === key ? null : key;
    setOpenMine(next);
    if (next) retry(compId);
  };
  // the dancer's PDF through the isolated client: an ended session turns the
  // page into the public view instead of logging the visitor out
  const print = signedIn
    ? async (compId, dancerId) => {
      const outcome = await printWithSession(`${BASE()}/feis/results/download/pdf?compId=${compId}&dancerId=${dancerId}`, setPrinting);
      if (outcome === "auth") downgrade();
    }
    : null;

  // ---- render ----
  const levels = levelsOf(comps);
  const lvl = levels.includes(level) ? level : "All";
  const allList = comps
    .filter((c) => lvl === "All" || c.group === lvl)
    .filter((c) => {
      const mineNames = mineList.filter((d) => d.comps.includes(c)).map((d) => d.name);
      // result rows count only while the competition is published: the loaded
      // (viewer) result, else the event's frozen public results
      const r = results[c._id];
      const rows = !c.results_published ? null : isLoaded(r) ? r : bulk?.[c._id] || null;
      return compMatches(c, q, rows, mineNames);
    });
  const mineHereOf = (c) => mineList.filter((d) => d.comps.some((x) => x._id === c._id)).map((d) => ({ id: d.id, name: d.name }));
  const searchingResults = q.trim().length >= 2 && bulk === null && published.length > 0;
  const studentList = mineList.filter((d) => includesFolded(d.name, tq));

  const header = (
    <>
      <button type="button" className="er-back" onClick={() => window.history.back()}><span className="msr" aria-hidden="true">chevron_left</span>Back</button>
      <div className="er-ph">
        {event?.logo ? <img className="er-logo" src={event.logo} alt="" /> : <div className="er-logo"><span className="msr" aria-hidden="true">emoji_events</span></div>}
        <div>
          <h1>{event?.name || "Results"}</h1>
          <div className="er-sub">
            {event?.feis_date && <span><span className="msr" aria-hidden="true">calendar_today</span>{moment(formatDate(event.feis_date)).format("dddd, MMMM D, YYYY")}</span>}
            {(event?.venue_city || event?.state) && <span><span className="msr" aria-hidden="true">location_on</span>{[event?.venue_city, event?.state].filter(Boolean).join(", ")}</span>}
          </div>
          {eventDay && updatedAt && (
            <div className="er-live"><span className="er-dot" />Results update automatically · Updated {moment(updatedAt).format("h:mm a")}</div>
          )}
        </div>
      </div>
    </>
  );

  const allResults = (
    <>
      <div className="er-toolbar">
        <label className="er-search">
          <span className="msr" aria-hidden="true">search</span>
          <input
            aria-label="Search competitions, dancers or card numbers"
            placeholder="Competition, dancer, school or card number"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        {levels.length > 1 && (
          <div className="er-chips" role="group" aria-label="Level">
            {["All", ...levels].map((l) => (
              <button key={l} type="button" className={`er-chip ${lvl === l ? "on" : ""}`} aria-pressed={lvl === l} onClick={() => setLevel(l)}>{l}</button>
            ))}
          </div>
        )}
        {searchingResults && <div className="er-hint">Searching all results…</div>}
      </div>
      {allList.length ? allList.map((c) => (
        <CompetitionCard
          key={c._id}
          comp={c}
          result={results[c._id]}
          open={openComp === c._id}
          onToggle={() => toggleComp(c._id)}
          onRetry={() => retry(c._id)}
          view={view}
          mineHere={family ? mineHereOf(c) : []}
          justPublished={eventDay && c.results_published && isJustPublished(c._id, { flipped, publishedAt: results[c._id]?.publishedAt || bulk?.[c._id]?.publishedAt })}
          onPrint={print}
        />
      )) : <div className="er-empty">{comps.length ? "No competitions match your search." : "Results are not published yet."}</div>}
    </>
  );

  // the same level chips as All Results, over the judge's own competitions
  // (one shared choice, so switching tabs keeps it; Devin 2026-10-28)
  const judgedLevels = levelsOf(judgedList);
  const jlvl = judgedLevels.includes(level) ? level : "All";
  const judgedShown = judgedList.filter((c) => jlvl === "All" || c.group === jlvl);
  const judgedResults = (
    <>
      {judgedLevels.length > 1 && (
        <div className="er-toolbar">
          <div className="er-chips" role="group" aria-label="Level">
            {["All", ...judgedLevels].map((l) => (
              <button key={l} type="button" className={`er-chip ${jlvl === l ? "on" : ""}`} aria-pressed={jlvl === l} onClick={() => setLevel(l)}>{l}</button>
            ))}
          </div>
        </div>
      )}
      {judgedShown.map((c) => (
        <JudgedCard
          key={c._id}
          comp={c}
          result={judgeResults[c._id]}
          open={openJudged === c._id}
          onToggle={() => toggleJudged(c._id)}
          onRetry={() => loadJudged(c._id)}
          picked={openJudged === c._id ? picked : null}
          onPick={(dancerId) => setPicked((p) => (p === dancerId ? null : dancerId))}
        />
      ))}
    </>
  );

  const myResults = (
    <>
      {view === "teacher" && (
        <div className="er-toolbar">
          <label className="er-search">
            <span className="msr" aria-hidden="true">search</span>
            <input aria-label="Find a student" placeholder="Find a student" value={tq} onChange={(e) => setTq(e.target.value)} />
          </label>
        </div>
      )}
      {(view === "teacher" ? studentList : mineList).length
        ? <DancerCards list={view === "teacher" ? studentList : mineList} results={results} openKey={openMine} onToggle={toggleMine} onRetry={retry} onPrint={print} />
        : <div className="er-empty">{view === "teacher" ? "No students match your search." : "Your dancers have no competitions at this event."}</div>}
    </>
  );

  return (
    <>
      {(initialLoading || printing) && <Loader />}
      <ParentHeader />
      <div className="oir er">
        {!initialLoading && (
          <div className="er-wrap">
            {header}
            {(family || judgedList.length > 0) && (
              <div className="er-tabs" role="tablist">
                {family && (
                  <button type="button" role="tab" aria-selected={shownTab === "mine"} className={`er-tab ${shownTab === "mine" ? "on" : ""}`} onClick={() => setTab("mine")}>
                    {view === "teacher" ? "Your students" : "My Results"} <span className="er-tcount">{mineList.length}</span>
                  </button>
                )}
                {judgedList.length > 0 && (
                  <button type="button" role="tab" aria-selected={shownTab === "judged"} className={`er-tab ${shownTab === "judged" ? "on" : ""}`} onClick={() => setTab("judged")}>
                    My adjudications <span className="er-tcount">{judgedList.length}</span>
                  </button>
                )}
                <button
                  type="button"
                  role="tab"
                  aria-selected={shownTab === "all" || (shownTab === "mine" && !family)}
                  className={`er-tab ${shownTab === "all" || (shownTab === "mine" && !family) ? "on" : ""}`}
                  onClick={() => setTab("all")}
                >
                  All Results
                </button>
              </div>
            )}
            {shownTab === "judged" && judgedList.length > 0 ? judgedResults : family && shownTab === "mine" ? myResults : allResults}
          </div>
        )}
      </div>
      <Help />
    </>
  );
};

export default EventResults;
